import { AppError } from '../core';
import { OperationState } from '@imagise/generated-imagise-cloud-api-client';

export interface ErrorReportAttachment {
  name: string;
  blob: Blob;
  inputIndex?: number;
}

export interface ErrorReportOperation {
  operation: string;
  endpoint?: string;
  method?: string;
  status?: number;
  requestId?: string;
  operationId?: string;
  parameters?: Readonly<Record<string, string | number | boolean | null>>;
  files?: readonly ErrorReportAttachment[];
}

export const MAX_REPORT_FILES = 10;
export const MAX_REPORT_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_REPORT_TOTAL_FILE_BYTES = 20 * 1024 * 1024;

/** Decode only known request image fields. Never fetch an arbitrary URL to collect a report. */
export function attachmentFromBase64(base64: string, name: string, mimeType = 'application/octet-stream'): ErrorReportAttachment | null {
  try {
    const data = base64.match(/^data:([^;,]+);base64,(.*)$/s);
    const encoded = data?.[2] ?? base64;
    const raw = atob(encoded);
    const bytes = Uint8Array.from(raw, character => character.charCodeAt(0));
    return { name, blob: new Blob([bytes], { type: data?.[1] ?? mimeType }) };
  } catch {
    return null;
  }
}

/** Known API envelopes may report failure even with HTTP 200. */
export function assertSuccessfulOperationResponse(response: unknown): void {
  if (!response || typeof response !== 'object' || response instanceof Blob) return;
  const body = response as Record<string, unknown>;
  const success = body['IsSuccess'] ?? body['isSuccess'];
  const state = body['State'] ?? body['state'];
  const asyncFailure = (typeof body['operationId'] === 'string' || typeof body['OperationId'] === 'string') && state === OperationState.Error;
  const detail = body['Error'] ?? body['error'] ?? (asyncFailure ? body['result'] ?? body['Result'] : undefined);
  const failed = success === false || asyncFailure || (typeof state === 'string' && /^(failed|error)$/i.test(state)) ||
    (success !== true && detail != null && detail !== '' && detail !== false);
  if (!failed) return;
  const message = typeof detail === 'string' ? detail : 'The image operation failed.';
  throw Object.assign(new Error(message), { name: 'OperationResponseError', error: body });
}

/** HTTP errors need an explicit body adapter; HttpErrorResponse.message alone drops diagnostics. */
export function operationAppError(error: unknown): AppError {
  const record = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  const body = record['error'];
  const details = body && typeof body === 'object' && !(body instanceof Blob)
    ? body as Record<string, unknown> : {};
  const nested = details['Error'] ?? details['error'] ?? details;
  const diagnostic = nested && typeof nested === 'object' ? nested as Record<string, unknown> : {};
  const message = diagnostic['ExceptionMessage'] ?? diagnostic['detail'] ?? diagnostic['title'] ??
    (typeof body === 'string' ? body : record['message']);
  return {
    ExceptionMessage: typeof message === 'string' ? message : 'The request could not be completed.',
    Type: typeof diagnostic['Type'] === 'string' ? diagnostic['Type'] : 'OperationError',
    Details: typeof diagnostic['Details'] === 'string' ? diagnostic['Details'] :
      typeof record['stack'] === 'string' ? record['stack'] : '',
    ...(diagnostic['InnerException'] && typeof diagnostic['InnerException'] === 'object'
      ? { InnerException: boundedInnerError(diagnostic['InnerException'], 0) } : {}),
  };
}

function boundedInnerError(value: unknown, depth: number): AppError {
  const record = value as Record<string, unknown>;
  return {
    ExceptionMessage: typeof record['ExceptionMessage'] === 'string' ? record['ExceptionMessage'] : 'Nested request error',
    Type: typeof record['Type'] === 'string' ? record['Type'] : 'OperationError',
    Details: typeof record['Details'] === 'string' ? record['Details'] : '',
    ...(depth < 6 && record['InnerException'] && typeof record['InnerException'] === 'object'
      ? { InnerException: boundedInnerError(record['InnerException'], depth + 1) } : {}),
  };
}

export function readReportFile(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => reader.result instanceof ArrayBuffer ? resolve(reader.result) : reject(new Error('File read failed'));
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsArrayBuffer(blob);
  });
}
